import { Badge, styled, Typography } from '@mui/material';
import { ReactNode, useState } from 'react';
import IconButton from '@/component/Button/IconButton';
import Translate from '@/component/Translate';
import { useTexts } from '@/feature/lang';
import { Icon } from '@/icon/Icon';

const ReferenceText = styled('div', {
  name: 'Chatbot',
  slot: 'ReferenceText',
})({
  display: 'flex',
  justifyContent: 'center',
});

interface ReferencePaginationProps {
  pageNumber: number;
  pageIndex: number;
  pages: number[];
  onPrevious: () => void;
  previousEnabled: boolean;
  onNext: () => void;
  nextEnabled: boolean;
  isDefault: boolean;
}

export const useReferencePagination = (
  pages: number[],
  defaultPage?: number
): ReferencePaginationProps => {
  const defaultIndex = pages.findIndex((p) => p === defaultPage);
  const initialIndex = defaultIndex !== -1 ? defaultIndex : 0;
  const [pageIndex, setPageIndex] = useState(initialIndex);

  return {
    pageNumber: pages[pageIndex],
    pageIndex,
    pages,
    onPrevious: () => setPageIndex(pageIndex - 1),
    previousEnabled: pageIndex > 0,
    onNext: () => setPageIndex(pageIndex + 1),
    nextEnabled: pageIndex + 1 < pages.length,
    isDefault: pageIndex === defaultIndex && pages.length > 1,
  };
};

export const ReferencePagination = ({
  pageNumber,
  pageIndex,
  pages,
  onPrevious,
  previousEnabled,
  onNext,
  nextEnabled,
  isDefault,
}: ReferencePaginationProps) => {
  const texts = useTexts().component.chatbot;

  return (
    <>
      <IconButton size="small" onClick={onPrevious} disabled={!previousEnabled}>
        <Icon name="chevron_left" size="small" />
      </IconButton>
      <ReferenceText>
        <ReferencePageBadge isDefault={isDefault}>
          <Typography variant="body2" sx={{ m: 0.5 }}>
            <Translate text={texts.reference.page} />
            {` ${pageNumber} (${pageIndex + 1}/${pages.length})`}
          </Typography>
        </ReferencePageBadge>
      </ReferenceText>
      <IconButton size="small" onClick={onNext} disabled={!nextEnabled}>
        <Icon name="chevron_right" size="small" />
      </IconButton>
    </>
  );
};

const ReferencePageBadge = ({
  isDefault,
  children,
}: {
  isDefault: boolean;
  children: ReactNode;
}) =>
  isDefault ? (
    <Badge color="secondary" variant="dot">
      {children}
    </Badge>
  ) : (
    <>{children}</>
  );
